import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  FieldType,
  FilterFormDataModel,
  FormField,
  LufthansaGroupID,
  UserPreferencesService,
} from '@thehubz/client/core';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, MediaLists, TerritoryCategories, TerritoryCategoriesLufthansaGroup, TerritoryList } from '@thehubz/shared/model';
import { Observable, Subscription } from 'rxjs';
import { NavigationTabService } from '../navigation-tab-link/navigation-tab.service';
import { FilterFormService } from './api-calls/filter-form.service';

export type FilterType = 'project' | 'task' | 'asset' | 'custom';
export type UsersFieldType = 'createdBy' | 'currentlyWithUser';

@Component({
  selector: 'sp-filter-forms',
  templateUrl: './filter-forms.component.html',
  styleUrls: ['./filter-forms.component.scss'],
})
export class FilterFormsComponent implements OnInit, OnDestroy {
  @Input() formData!: FilterFormDataModel;
  @Input() filterType!: FilterType;
  @Input() usersFieldType: UsersFieldType = 'createdBy';
  @Input() roles!: string[];
  @Input() isCustomTab?: boolean;
  @Input() className?: string;
  @Input() sharingClientFlag?: boolean;
  @Input() selectedFilterObject?: AnyType;
  @Input() resetFilterForm!: Observable<{ _id: string; name: string; key: string } | undefined>;
  @Output() clickApplyFilter = new EventEmitter();
  @Output() dialogResult = new EventEmitter<AnyType>();
  @Output() dropDownChange = new EventEmitter<AnyType>();

  filterForm: UntypedFormGroup;
  callFromDialog!: boolean;
  dialogHeader!: string | undefined;

  resetFilterFormSubscription?: Subscription;

  filterProjectFormData: FilterFormDataModel = {} as FilterFormDataModel;

  isGuidedTourEnabled? = false;
  isLufthansaGroupClient = false;
  initialFormData?: FilterFormDataModel;
  readonly brandingKey: CustomClients;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    @Optional() public dialogRef: MatDialogRef<FilterFormsComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: {
      filterProjectFormData: FilterFormDataModel;
      dialogHeader: string;
      callFromDialog: boolean;
      filterType: FilterType;
      usersFieldType: UsersFieldType;
      selectedFilterData: AnyType;
    },
    private _formBuilder: UntypedFormBuilder,
    protected filterFormService: FilterFormService,
    private commonUtilityLib: CommonUtilityLib,
    private userPreferences: UserPreferencesService,
    private navigationTabService: NavigationTabService,
  ) {
    this.brandingKey = env.brandingKey;
    this.filterForm = this._formBuilder.group({});
    this.isGuidedTourEnabled = env?.isGuidedTourEnabled;
  }

  ngOnInit(): void {
    /* Dialog initialization */
    this.init();
    this.initialFormData = JSON.parse(JSON.stringify(this.formData));
    /* ********** */
    switch (this.filterType) {
      case 'task': {
        this.filterFormService.getAgencies(this.filterProjectFormData, this.roles);
        this.filterFormService.fetchTeamsList();
        this.filterFormService.fetchACBProjectList(this.filterProjectFormData, this.selectedFilterObject);
        this.filterFormService.getWorkflowList(this.filterProjectFormData);
        this.filterFormService.getCreateByUserList(this.filterProjectFormData);
        break;
      }
      case 'project': {
        this.filterFormService.getAgencies(this.filterProjectFormData, this.roles);
        this.filterFormService.fetchTeamsList();
        break;
      }
      case 'asset': {
        if (!this.sharingClientFlag) {
          if (this.filterProjectFormData.customClient === CustomClients.Vorwerk) {
            this.filterFormService.getAgencies(this.filterProjectFormData, this.roles);
            this.filterFormService.getRelationType(this.filterProjectFormData);
          } else if (
            this.filterProjectFormData.customClient === CustomClients.LufthansaMediacenter ||
            this.filterProjectFormData.customClient === CustomClients.LufthansaMedialounge
          ) {
            this.filterFormService.getBrands(this.filterProjectFormData, this.roles);
          } else if (this.filterProjectFormData.customClient === CustomClients.SEAT) {
            // Do nothing
          } else {
            this.filterFormService.getClients(this.filterProjectFormData, this.roles);
            this.filterFormService.getRelationType(this.filterProjectFormData);
          }
        }
        break;
      }
    }
    this.filterFormGroup();
    this.patchFilterFormGroup();
    // reset filter form on clearing individual bubble or clear all bubble
    this.resetFilterFormSubscription = this.resetFilterForm?.subscribe((response) => {
      this.resetFormOnFilterBubble(response);
    });
  }

  /* Dialog initialization */
  init() {
    this.filterProjectFormData = this.formData ? this.formData : this.data.filterProjectFormData;
    this.callFromDialog = this.data && this.data.callFromDialog ? this.data.callFromDialog : false;
    this.dialogHeader = this.data && this.data.dialogHeader ? this.data.dialogHeader : undefined;
    this.filterType = this.filterType ? this.filterType : this.data.filterType;
    this.usersFieldType = this.data?.usersFieldType ? this.data.usersFieldType : this.usersFieldType;
    this.selectedFilterObject = this.data?.selectedFilterData ? this.data.selectedFilterData : this.selectedFilterObject;
  }

  resetFormOnFilterBubble(response: { _id: string; name: string; key: string } | undefined) {
    // handle undefined on 'clear all' chip and reset the form
    if (!response) {
      this.resetForm();
      return;
    }
    if (response) {
      const filterValue = this.filterForm.get(response.key)?.value;
      const formIndex = this.filterProjectFormData.formFields.findIndex((field) => field.formFieldName === response.key);
      // Handle the individual dropdown selection on each chip removal
      // reinitialize formFields options to update the sp-mat-select-search dataSource
      if (formIndex !== -1 && this.filterProjectFormData.formFields[formIndex].options)
        this.filterProjectFormData.formFields[formIndex].options = JSON.parse(JSON.stringify(this.filterProjectFormData.formFields[formIndex].options));
      if (Array.isArray(filterValue)) {
        const modifiedFormControl = this.commonUtilityLib.removeObjectFromArray(filterValue, response._id);
        this.filterForm.get(response.key)?.setValue(modifiedFormControl);
        if (filterValue?.length === 0) this.filterForm.get(response.key)?.setValue('');
      } else {
        this.filterForm.get(response.key)?.setValue('');
        // For single value dropdown in client
        if (response.key === 'agency') this.filterForm.get('client')?.setValue('');
        this.dropDownChange.emit({ formControlName: response.key, event: { value: undefined } });
      }
    }
  }

  /**
   * Patch the filter form
   */
  async patchFilterFormGroup() {
    for (const key in this.selectedFilterObject) {
      this.filterForm.patchValue({
        [key]: this.selectedFilterObject[key],
      });
      const index = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, key);
      if (key === 'agency') {
        await this.filterFormService.getAgencies(this.filterProjectFormData, this.roles);
        await this.filterFormService.fetchTeamsList();
        this.onValueChange({ value: this.selectedFilterObject[key] }, this.filterProjectFormData.formFields[index], key);
      } else if (key === 'client' || key === 'currentlyWithTeam') {
        await this.filterFormService.getClients(this.filterProjectFormData, this.roles);
        this.onValueChange({ value: this.selectedFilterObject[key] }, this.filterProjectFormData.formFields[index], key);
      } else if (key === 'brand' || key === 'product') {
        this.dropDownChange.emit({ formControlName: key, event: { value: this.selectedFilterObject[key] } });
      }
    }
  }

  onValueChange(event: AnyType, fieldObject: AnyType, formControlName: string) {
    let selectedOption;
    const eventValueId = event.value.id ? event.value.id : event.value._id;
    const idx = fieldObject && fieldObject.options.findIndex((obj: AnyType) => obj._id === eventValueId);
    if (idx != -1) {
      selectedOption = fieldObject && fieldObject.options[idx];
    }
    if (fieldObject && fieldObject['functionCall']) {
      if (formControlName == 'agency') {
        if (!this.sharingClientFlag) {
          this.filterFormService.fetchCompaniesByAgency(this.filterProjectFormData, this.filterForm, selectedOption, event.source?.singleValue);
          this.filterFormService.fetchTeamsByAgency(this.filterProjectFormData, eventValueId, this.filterForm, event.source?.singleValue);
        }
      } else if (formControlName == 'client') {
        this.isLufthansaGroupClient = eventValueId === LufthansaGroupID ? true : false;

        const index = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'brand');
        if (event?.source && !event?.source?.singleValue) this.filterForm.controls['brand'].setValue('');
        if (!this.sharingClientFlag) {
          this.filterProjectFormData.formFields[index]['options'] = selectedOption['brands'];
        }
        const contentFieldIndex = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'lufthansaContent') || -1;
        const productFieldIndex = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'lufthansaProduct') || -1;
        const territoriesFieldIndex = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, AssetMetadata.Others.TERRITORY) || -1;
        if (contentFieldIndex > -1 && productFieldIndex > -1 && territoriesFieldIndex > -1) {
          const territoriesList = this.isLufthansaGroupClient
            ? [...TerritoryCategories, ...TerritoryCategoriesLufthansaGroup, ...TerritoryList]
            : [...TerritoryCategories, ...TerritoryList];
          const matDividerLength = this.isLufthansaGroupClient
            ? [...TerritoryCategories, ...TerritoryCategoriesLufthansaGroup].length - 1
            : [...TerritoryCategories].length - 1;
          if (this.isLufthansaGroupClient) {
            this.filterProjectFormData.formFields[contentFieldIndex].visible = true;
            this.filterProjectFormData.formFields[territoriesFieldIndex].options = territoriesList;
            this.filterProjectFormData.formFields[territoriesFieldIndex].matDivider = matDividerLength;
          } else {
            this.filterProjectFormData.formFields[contentFieldIndex].visible = false;
            this.filterProjectFormData.formFields[productFieldIndex].visible = false;
            this.filterProjectFormData.formFields[territoriesFieldIndex].options = territoriesList;
            this.filterProjectFormData.formFields[territoriesFieldIndex].matDivider = matDividerLength;
          }
        }
      } else if (formControlName == 'lufthansaContent') {
        const productFieldIndex = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'lufthansaProduct');
        if (productFieldIndex !== undefined && productFieldIndex > -1) {
          if (event.value?._id === 'product') {
            this.filterProjectFormData.formFields[productFieldIndex].visible = true;
          } else {
            this.filterProjectFormData.formFields[productFieldIndex].visible = false;
          }
        }
      } else if (formControlName == 'buyoutRightsTitle') {
        const secondLevelTypeIndex = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'buyoutRightsSecondLevelType');
        if (secondLevelTypeIndex !== undefined && secondLevelTypeIndex > -1) {
          const newOptions = this.initialFormData?.formFields[secondLevelTypeIndex].options.filter((option: AnyType) => option.firstLvl === eventValueId) || [];
          const formFieldName = this.filterProjectFormData.formFields[secondLevelTypeIndex].formFieldName;
          if (newOptions.length > 0) {
            this.filterProjectFormData.formFields[secondLevelTypeIndex].visible = true;
            this.filterProjectFormData.formFields[secondLevelTypeIndex].options = newOptions;
            this.filterProjectFormData.formFields[secondLevelTypeIndex].fieldLabel = event.value.name;
          } else {
            this.filterProjectFormData.formFields[secondLevelTypeIndex].visible = false;
            this.filterForm.controls[formFieldName].setValue('');
          }
        }
      } else if (['territory', 'media'].includes(formControlName)) {
        if (this.brandingKey === CustomClients.Standard) {
          const formControl = this.filterForm.controls[formControlName];
          const selectedValues: AnyType[] = event.value;
          const territories = TerritoryList;
          const oldFormValue: AnyType[] = formControl?.getRawValue() || [];
          setTimeout(() => {
            const updatedFormValue: AnyType[] = formControl?.getRawValue() || [];
            const removedFormValue = this.getRemovedValues(oldFormValue, selectedValues);
            const quickSelectionMap: Record<
              string,
              {
                _id: string;
                name: string;
                categories: string[];
              }[]
            > = {
              [AssetMetadata.Others.TERRITORY]: territories,
              [AssetMetadata.General.MEDIA]: MediaLists,
            };

            const quickSelectionList = quickSelectionMap[formControlName] ?? [];
            let filteredList: { _id: string; name: string; categories?: string[] }[] = [];
            if (removedFormValue.length) {
              filteredList = this.getTerritoriesToRemove(quickSelectionList, removedFormValue);
              formControl?.patchValue(updatedFormValue.filter((value) => !filteredList.some((territory) => territory._id === value._id)));
            } else {
              filteredList = this.getTerritoriesToAdd(quickSelectionList, selectedValues);
              const uniqueValues = [...new Map([...updatedFormValue, ...filteredList].map((item) => [item._id, item])).values()];
              formControl?.patchValue(uniqueValues);
            }
          }, 0);
        }
      } else {
        const index = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, 'brand');
        this.filterProjectFormData.formFields[index]['options'] = selectedOption['brands'];
        if (event?.source && !event?.source?.singleValue) this.filterForm.controls['brand'].setValue('');
      }
    }
    if (formControlName === 'currentlyWithTeam') {
      this.filterFormService.usersCurrentlyWithTeam(this.filterProjectFormData, this.filterForm, selectedOption, this.usersFieldType);
    }
    if (this.filterType === 'asset') {
      if (formControlName === 'assetType') {
        this.filterFormService.updateFileType(this.filterProjectFormData, eventValueId);
      }
    }
    setTimeout(() => {
      const filterData = this.filterForm.getRawValue();
      if (this.filterType === 'task') {
        this.filterFormService.fetchACBProjectList(this.filterProjectFormData, filterData);
      }
    });
    this.dropDownChange.emit({ formControlName, event });
  }

  getRemovedValues(oldValues: AnyType[], newValues: AnyType[]): AnyType[] {
    return oldValues.filter((oldValue) => !newValues.some((value) => oldValue._id === value._id));
  }

  getTerritoriesToRemove(territories: AnyType[], removedValues: AnyType[]): AnyType[] {
    return territories.filter((territory) => removedValues.some((value) => territory.categories.includes(value._id)));
  }

  getTerritoriesToAdd(territories: AnyType[], selectedValues: AnyType[]): AnyType[] {
    return territories.filter((territory) => selectedValues.some((value) => territory.categories.includes(value._id)));
  }

  /**
   * Create filter form group
   */
  filterFormGroup() {
    this.filterForm = new UntypedFormGroup({});
    for (const fieldObject of this.filterProjectFormData['formFields']) {
      let fieldValueObject = [];
      if (fieldObject['formFieldName'] && fieldObject['formFieldName'] !== '') {
        const fieldName = fieldObject['formFieldName'];
        if (fieldObject['fieldType'] === 'checkbox') {
          if ((fieldObject.value || 'value' in fieldObject) && !fieldObject.options) {
            fieldValueObject = fieldObject.value;
            const control: UntypedFormControl = this.createFormFieldControl(fieldObject, fieldName);
            this.filterForm.addControl(fieldName, control);
          }
          if (fieldObject.value && fieldObject.options) {
            const control: UntypedFormArray = this.buildFormArray(fieldObject, fieldValueObject);
            this.filterForm.addControl(fieldName, control);
          }
        } else {
          const control: UntypedFormControl = this.createFormFieldControl(fieldObject, fieldName);
          if (fieldObject['disable']) {
            control.disable();
          }
          this.filterForm.addControl(fieldName, control);
          if (fieldObject['formGroup']) {
            const control: UntypedFormControl = new UntypedFormControl('', []);
            this.filterForm.addControl(fieldName + '_formGroup', control);
          }
        }
      }
    }
  }

  createFormFieldControl(fieldObject: FormField, fieldName: string) {
    const fieldValidation = [];
    if (fieldObject['mandatory']) {
      fieldValidation.push(Validators.required);
    }
    if (fieldObject['maxlength']) {
      fieldValidation.push(Validators.maxLength(fieldObject['maxlength']));
    }
    if (fieldObject['minlength']) {
      fieldValidation.push(Validators.minLength(fieldObject['minlength']));
    }
    if (fieldObject['regex']) {
      fieldValidation.push(this.validateFormat(fieldObject['regex']));
    }
    let fieldPatchValue = '';
    if (this.filterProjectFormData && this.filterProjectFormData['applicationData']) {
      if (this.filterProjectFormData['applicationData'][fieldName] && this.filterProjectFormData['applicationData'][fieldName] != '') {
        fieldPatchValue = this.filterProjectFormData['applicationData'][fieldName];
      }
    }
    return new UntypedFormControl(fieldPatchValue, fieldValidation as ValidatorFn[]);
  }

  /**
   * build form array
   * @param formArray
   * @param buildFormArray
   * @returns
   */
  buildFormArray(formArray: AnyType, buildFormArray?: AnyType) {
    const arr = formArray.options.map((option: AnyType) => {
      if (buildFormArray && buildFormArray.length > 0 && buildFormArray.includes(option.id)) {
        option.selected = true;
      }
      return new UntypedFormControl(option.selected ? option.id : null);
    });
    return new UntypedFormArray(arr);
  }

  /**
   * validate form filed with regex
   * @param regexString
   * @returns
   */
  validateFormat(regexString: string) {
    const regex = new RegExp(regexString);
    return (input: UntypedFormControl) => {
      if (input.value) {
        return regex.test(input.value) ? null : { validformat: true };
      }
      return null;
    };
  }
  /**
   * Reset form data
   */
  resetFormData() {
    const skipResetFields = ['status', 'assetType', 'annotationStatus'];
    // condition specific to custom client arval
    if (this.filterProjectFormData.customClient === CustomClients.Arval) {
      const dropDownFieldsToSkip = this.filterProjectFormData.formFields
        .filter((field) => field.fieldType === FieldType.Dropdown)
        .map((field) => field.formFieldName);
      skipResetFields.push(...dropDownFieldsToSkip);
    }
    if (this.filterProjectFormData.customClient === CustomClients.Vorwerk) {
      skipResetFields.push('channel', 'subject', 'agency', 'creatorDepartment', 'buyoutRightsTitle', 'licenseRegion', 'relationType');
    }
    if (this.filterProjectFormData.customClient === CustomClients.VKB) {
      skipResetFields.push('pursue', 'assetTypeCustom', 'imageArt');
    }
    if (this.filterProjectFormData.customClient === CustomClients.WestLotto) {
      skipResetFields.push('mediumType', 'formatType');
    }
    if (this.filterProjectFormData.customClient === CustomClients.SEAT) {
      skipResetFields.push('brandMarke', 'brandModel', 'specialModel', 'drive', 'trim', 'mediaType', 'channels');
    }
    if (this.sharingClientFlag) {
      skipResetFields.push('client', 'brand');
    }
    this.filterProjectFormData.formFields
      .filter((ele) => Object.prototype.hasOwnProperty.call(ele, 'options') && !skipResetFields.includes(ele.formFieldName || ''))
      .forEach((ele) => {
        const index = this.commonUtilityLib.findArrayIndex(this.filterProjectFormData, ele.formFieldName || '');
        this.filterProjectFormData.formFields[index]['options'] = [];
      });
  }

  /**
   * Capture click event on reset button
   */
  resetForm() {
    this.filterFormGroup();
    this.resetFormData();
    this.clickApplyFilter.emit();
    this.dropDownChange.emit();
    if (this.callFromDialog) {
      this.dialogRef.close();
    }
  }

  /**
   * Capture click event on filter data button
   */
  filterData() {
    const filterData = this.filterForm.value;
    this.clickApplyFilter.emit(filterData);
    if (this.callFromDialog) {
      this.dialogRef.close(filterData);
    }
  }

  /**
   * Open filter form dialog and create tab
   */
  addTabs() {
    const filterFormData = this.filterForm.value;
    const selectedFilterData: AnyType = Object.fromEntries(Object.entries(filterFormData).filter(([_, v]) => v != ''));
    const dialogDataConfig = { filterType: this.filterType, usersFieldType: this.usersFieldType };
    const dialogRef = this.navigationTabService.addTabs(this.filterProjectFormData, dialogDataConfig, selectedFilterData);
    dialogRef.subscribe((result) => {
      if (result) {
        this.userPreferences.filteredData.next({ filterResult: result, changeType: this.filterType });
      }
    });
  }

  /**
   * Remove chip from multi select
   * @param value
   */
  removeChip(event: AnyType) {
    const formControlValue = this.filterForm.get(event.formControlName)?.value;
    const modifiedFormControl = this.commonUtilityLib.removeObjectFromArray(formControlValue, event.list.id ?? event.list._id);
    this.filterForm.get(event.formControlName)?.patchValue(modifiedFormControl);
  }

  checkboxChanged(event: MatCheckboxChange, formControlName: string) {
    this.dropDownChange.emit({ formControlName, event });
  }

  ngOnDestroy(): void {
    this.resetFilterFormSubscription?.unsubscribe();
  }
}
